import { useState, useEffect, useCallback, useRef } from "react";
import { Accessibility, Plus, Minus, Type, Underline, Eye, X, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

interface AccessibilitySettings {
  fontSize: number; // 0 = normal, positive = larger, negative = smaller
  highContrast: "none" | "black-white" | "black-yellow";
  grayscale: boolean;
  underlineLinks: boolean;
  readableFont: boolean;
}

interface Position {
  x: number;
  y: number;
}

const defaultSettings: AccessibilitySettings = {
  fontSize: 0,
  highContrast: "none",
  grayscale: false,
  underlineLinks: false,
  readableFont: false,
};

const normalizeSettings = (input: unknown): AccessibilitySettings => {
  const obj = (input ?? {}) as Partial<Record<keyof AccessibilitySettings, unknown>>;

  const fontSizeRaw = typeof obj.fontSize === "number" ? obj.fontSize : 0;
  const fontSize = Number.isFinite(fontSizeRaw) ? Math.max(-3, Math.min(5, fontSizeRaw)) : 0;

  const highContrastRaw = obj.highContrast;
  const highContrast: AccessibilitySettings["highContrast"] =
    highContrastRaw === "black-white" || highContrastRaw === "black-yellow" || highContrastRaw === "none"
      ? highContrastRaw
      : "none";

  return {
    fontSize,
    highContrast,
    grayscale: obj.grayscale === true,
    underlineLinks: obj.underlineLinks === true,
    readableFont: obj.readableFont === true,
  };
};

const getSavedSettings = (): AccessibilitySettings => {
  try {
    const saved = localStorage.getItem("accessibilitySettings");
    return saved ? normalizeSettings(JSON.parse(saved)) : defaultSettings;
  } catch {
    return defaultSettings;
  }
};

  
const getDefaultPosition = (): Position => ({
  x: 16, // left: 4 = 16px
  y: window.innerHeight - 96 - 48, // bottom-24 = 96px, button height = 48px
});

export const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState<AccessibilitySettings>(() => getSavedSettings());

  const [position, setPosition] = useState<Position>(() => {
    const saved = localStorage.getItem("accessibilityWidgetPosition");
    if (saved) {
      const parsed = JSON.parse(saved);
      // Validate position is still within viewport
      const maxX = window.innerWidth - 48;
      const maxY = window.innerHeight - 48;
      return {
        x: Math.min(Math.max(0, parsed.x), maxX),
        y: Math.min(Math.max(0, parsed.y), maxY),
      };
    }
    return getDefaultPosition();
  });
  
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Apply settings function
  const applySettings = useCallback((currentSettings: AccessibilitySettings) => {
    const root = document.documentElement;

    // Font size
    const baseFontSize = 16 + currentSettings.fontSize * 2;
    root.style.fontSize = `${baseFontSize}px`;

    // High contrast - only modify if needed
    const hasHighContrastBW = root.classList.contains("high-contrast-bw");
    const hasHighContrastBY = root.classList.contains("high-contrast-by");

    if (currentSettings.highContrast === "black-white" && !hasHighContrastBW) {
      root.classList.remove("high-contrast-by");
      root.classList.add("high-contrast-bw");
    } else if (currentSettings.highContrast === "black-yellow" && !hasHighContrastBY) {
      root.classList.remove("high-contrast-bw");
      root.classList.add("high-contrast-by");
    } else if (currentSettings.highContrast === "none" && (hasHighContrastBW || hasHighContrastBY)) {
      root.classList.remove("high-contrast-bw", "high-contrast-by");
    }

    // Grayscale - only toggle if state differs
    const hasGrayscale = root.classList.contains("grayscale-mode");
    if (currentSettings.grayscale !== hasGrayscale) {
      root.classList.toggle("grayscale-mode", currentSettings.grayscale);
    }

    // Underline links
    const hasUnderline = root.classList.contains("underline-links");
    if (currentSettings.underlineLinks !== hasUnderline) {
      root.classList.toggle("underline-links", currentSettings.underlineLinks);
    }

    // Readable font
    const hasReadableFont = root.classList.contains("readable-font");
    if (currentSettings.readableFont !== hasReadableFont) {
      root.classList.toggle("readable-font", currentSettings.readableFont);
    }
  }, []);

  // Apply on mount and when settings change
  useEffect(() => {
    applySettings(settings);
    // Persist the normalized settings so stale/partial saved values can't re-enable B/W on navigation.
    localStorage.setItem("accessibilitySettings", JSON.stringify(settings));
  }, [settings, applySettings]);

  // Save position to localStorage
  useEffect(() => {
    localStorage.setItem("accessibilityWidgetPosition", JSON.stringify(position));
  }, [position]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => ({
        x: Math.min(prev.x, window.innerWidth - 48),
        y: Math.min(prev.y, window.innerHeight - 48),
      }));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      posX: position.x,
      posY: position.y,
    };
  }, [position]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    setIsDragging(true);
    dragStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      posX: position.x,
      posY: position.y,
    };
  }, [position]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragStartRef.current) return;
      const deltaX = e.clientX - dragStartRef.current.x;
      const deltaY = e.clientY - dragStartRef.current.y;
      const newX = Math.min(Math.max(0, dragStartRef.current.posX + deltaX), window.innerWidth - 48);
      const newY = Math.min(Math.max(0, dragStartRef.current.posY + deltaY), window.innerHeight - 48);
      setPosition({ x: newX, y: newY });
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!dragStartRef.current) return;
      const touch = e.touches[0];
      const deltaX = touch.clientX - dragStartRef.current.x;
      const deltaY = touch.clientY - dragStartRef.current.y;
      const newX = Math.min(Math.max(0, dragStartRef.current.posX + deltaX), window.innerWidth - 48);
      const newY = Math.min(Math.max(0, dragStartRef.current.posY + deltaY), window.innerHeight - 48);
      setPosition({ x: newX, y: newY });
    };

    const handleEnd = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleEnd);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleEnd);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleEnd);
    };
  }, [isDragging]);

  const updateSetting = <K extends keyof AccessibilitySettings>(
    key: K,
    value: AccessibilitySettings[K]
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  }, []);

  return (
    <>
      {/* Floating Button */}
      <button
        ref={buttonRef}
        onClick={() => !isDragging && setIsOpen(!isOpen)}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
        className={cn(
          "fixed z-[90] h-12 w-12 rounded-full shadow-lg transition-all duration-300",
          "bg-primary text-primary-foreground hover:scale-110 hover:shadow-xl",
          "flex items-center justify-center",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
          isDragging ? "cursor-grabbing scale-110" : "cursor-grab"
        )}
        aria-label={isOpen ? "סגור תפריט נגישות" : "פתח תפריט נגישות (ניתן לגרור)"}
        aria-expanded={isOpen}
        aria-controls="accessibility-menu"
      >
        {isOpen ? (
          <X className="h-6 w-6" aria-hidden="true" />
        ) : (
          <Accessibility className="h-6 w-6" aria-hidden="true" />
        )}
      </button>

      {/* Accessibility Menu */}
      <div
        id="accessibility-menu"
        role="dialog"
        aria-label="אפשרויות נגישות"
        aria-modal="true"
        onKeyDown={handleKeyDown}
        style={{
          left: `${position.x}px`,
          top: `${Math.max(16, position.y - 320)}px`,
        }}
        className={cn(
          "fixed z-[90] w-72 bg-background rounded-2xl shadow-2xl border border-border transition-all duration-300",
          isOpen
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-4 pointer-events-none"
        )}
      >
        <div className="p-4" dir="rtl">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-border">
            <h2 className="font-bold text-lg flex items-center gap-2">
              <Accessibility className="h-5 w-5 text-primary" aria-hidden="true" />
              נגישות
            </h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetSettings}
              className="text-xs text-muted-foreground hover:text-foreground"
            >
              איפוס
            </Button>
          </div>

          {/* Options */}
          <div className="space-y-4">
            {/* Font Size */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Type className="h-4 w-4" aria-hidden="true" />
                גודל טקסט
              </label>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => updateSetting("fontSize", Math.max(-3, settings.fontSize - 1))}
                  disabled={settings.fontSize <= -3}
                  aria-label="הקטן טקסט"
                  className="h-10 w-10"
                >
                  <Minus className="h-4 w-4" aria-hidden="true" />
                </Button>
                <div className="flex-1 text-center text-sm font-medium bg-muted rounded-lg py-2">
                  {settings.fontSize === 0 ? "רגיל" : settings.fontSize > 0 ? `+${settings.fontSize}` : settings.fontSize}
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => updateSetting("fontSize", Math.min(5, settings.fontSize + 1))}
                  disabled={settings.fontSize >= 5}
                  aria-label="הגדל טקסט"
                  className="h-10 w-10"
                >
                  <Plus className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>
            </div>

            {/* High Contrast */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Eye className="h-4 w-4" aria-hidden="true" />
                ניגודיות גבוהה
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => updateSetting("highContrast", "none")}
                  className={cn(
                    "py-2 px-3 rounded-lg text-xs font-medium transition-all border",
                    settings.highContrast === "none"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-muted hover:bg-muted/80 border-transparent"
                  )}
                  aria-pressed={settings.highContrast === "none"}
                >
                  רגיל
                </button>
                <button
                  onClick={() => updateSetting("highContrast", "black-white")}
                  className={cn(
                    "py-2 px-3 rounded-lg text-xs font-medium transition-all border",
                    settings.highContrast === "black-white"
                      ? "bg-black text-white border-black"
                      : "bg-muted hover:bg-muted/80 border-transparent"
                  )}
                  aria-pressed={settings.highContrast === "black-white"}
                >
                  שחור-לבן
                </button>
                <button
                  onClick={() => updateSetting("highContrast", "black-yellow")}
                  className={cn(
                    "py-2 px-3 rounded-lg text-xs font-medium transition-all border",
                    settings.highContrast === "black-yellow"
                      ? "bg-black text-yellow-400 border-black"
                      : "bg-muted hover:bg-muted/80 border-transparent"
                  )}
                  aria-pressed={settings.highContrast === "black-yellow"}
                >
                  שחור-צהוב
                </button>
              </div>
            </div>

            {/* Grayscale */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium flex items-center gap-2">
                <Eye className="h-4 w-4" aria-hidden="true" />
                גווני אפור
              </label>
              <button
                role="switch"
                aria-checked={settings.grayscale}
                aria-label="הפעל גווני אפור"
                onClick={() => updateSetting("grayscale", !settings.grayscale)}
                className={cn(
                  "relative h-6 w-11 rounded-full transition-colors",
                  settings.grayscale ? "bg-primary" : "bg-muted"
                )}
              >
                <span
                  className={cn(
                    "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
                    settings.grayscale ? "right-0.5" : "right-5"
                  )}
                />
              </button>
            </div>

            {/* Underline Links */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium flex items-center gap-2">
                <Underline className="h-4 w-4" aria-hidden="true" />
                הדגש קישורים
              </label>
              <button
                role="switch"
                aria-checked={settings.underlineLinks}
                aria-label="הדגש קישורים בקו תחתון"
                onClick={() => updateSetting("underlineLinks", !settings.underlineLinks)}
                className={cn(
                  "relative h-6 w-11 rounded-full transition-colors",
                  settings.underlineLinks ? "bg-primary" : "bg-muted"
                )}
              >
                <span
                  className={cn(
                    "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
                    settings.underlineLinks ? "right-0.5" : "right-5"
                  )}
                />
              </button>
            </div>

            {/* Readable Font */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium flex items-center gap-2">
                <Type className="h-4 w-4" aria-hidden="true" />
                פונט קריא
              </label>
              <button
                role="switch"
                aria-checked={settings.readableFont}
                aria-label="הפעל פונט קריא"
                onClick={() => updateSetting("readableFont", !settings.readableFont)}
                className={cn(
                  "relative h-6 w-11 rounded-full transition-colors",
                  settings.readableFont ? "bg-primary" : "bg-muted"
                )}
              >
                <span
                  className={cn(
                    "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
                    settings.readableFont ? "right-0.5" : "right-5"
                  )}
                />
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 pt-3 border-t border-border">
            <Link
              to="/accessibility"
              className="text-xs text-primary hover:underline"
            >
              הצהרת נגישות מלאה
            </Link>
          </div>
        </div>
      </div>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[85] bg-black/20 backdrop-blur-sm"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </>
  );
};